'use client'

/**
 * Reusable workspace avatar with a credits-usage ring around it.
 *
 * Visual model:
 *  - SVG ring (rotated -90°) with two stacked <circle>s: a track + a progress
 *    arc whose strokeDashoffset is driven by `usagePercent` (0–100).
 *  - The ring color encodes usage tier: >50% green, >25% yellow, ≤25% red.
 *  - Inner <Avatar> for the workspace image / solid brand fallback.
 *  - Optional hover overlay surfaces the percent label (desktop popover trigger).
 *
 * Sizes: pass `size` in pixels. Defaults to 32 (matches the desktop sidebar
 * footer trigger). Stroke scales with `strokeRatio` (default 2.5/32 ≈ 7.8%).
 *
 * If `usagePercent` is null (still loading), the ring renders only the track
 * and the percent overlay is suppressed.
 */

import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

export type WorkspaceCreditsAvatarProps = {
  imageSrc?: string
  /** 0–100, or null when unknown / still loading. */
  usagePercent: number | null
  /** Tooltip — number of impressions left. */
  impressionsRemaining?: number
  /** Tooltip — total purchased lifetime. */
  impressionsTotal?: number
  /** Outer SVG square size in px. Default 32. */
  size?: number
  /** Ring stroke as a ratio of size. Default 2.5 / 32. */
  strokeRatio?: number
  /** Show the percent overlay on hover. Default true. */
  showHoverPercent?: boolean
  /** Extra wrapper className. */
  className?: string
}

export function WorkspaceCreditsAvatar({
  imageSrc,
  usagePercent,
  impressionsRemaining,
  impressionsTotal,
  size = 32,
  strokeRatio = 2.5 / 32,
  showHoverPercent = true,
  className,
}: WorkspaceCreditsAvatarProps) {
  const stroke = size * strokeRatio
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius

  const pct = usagePercent === null ? 0 : Math.max(0, Math.min(100, usagePercent))
  const dashOffset = circumference * (1 - pct / 100)
  const percentLabel =
    usagePercent === null ? null : `${Math.round(pct)}%`

  const ratio = usagePercent === null ? null : pct / 100
  const strokeColorClass =
    ratio === null
      ? 'stroke-atrium-border'
      : ratio > 0.5
        ? 'stroke-emerald-500'
        : ratio > 0.25
          ? 'stroke-yellow-500'
          : 'stroke-red-500'

  const tooltipText =
    impressionsTotal && impressionsTotal > 0
      ? `${(impressionsRemaining ?? 0).toLocaleString()} / ${impressionsTotal.toLocaleString()} impressions remaining`
      : undefined

  return (
    <div
      className={cn('relative shrink-0', className)}
      style={{ width: size, height: size }}
      title={tooltipText}
    >
      <svg
        aria-hidden
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
        style={{ width: size, height: size }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          className="stroke-atrium-border/80"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          className={cn(
            strokeColorClass,
            'transition-[stroke-dashoffset] duration-300 ease-out',
          )}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
        />
      </svg>
      <div
        className="absolute overflow-hidden rounded-full"
        style={{ inset: stroke + 1 }}
      >
        <Avatar className="size-full rounded-full">
          <AvatarImage src={imageSrc} className="object-cover" />
          <AvatarFallback className="flex size-full items-center justify-center rounded-full bg-accent-600 text-transparent">
            {'\u200b'}
          </AvatarFallback>
        </Avatar>
        {showHoverPercent && percentLabel ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-atrium-border/95 text-[9px] font-medium text-black opacity-0 transition-opacity duration-150 group-hover/workspace-trigger:opacity-100 dark:text-white">
            {percentLabel}
          </div>
        ) : null}
      </div>
    </div>
  )
}
